import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiEye } from 'react-icons/fi';
import { fetchAuditLogs } from '../api/auditService';

function toLabel(action = '') {
  return action
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase()) || 'Unknown';
}

function toneForAction(action = '') {
  const a = action.toLowerCase();
  if (a.includes('convert')) return 'converted';
  if (a.includes('assign') || a.includes('reassign')) return 'assigned';
  if (a.includes('delete') || a.includes('reject') || a.includes('lost')) return 'critical';
  if (a.includes('create') || a.includes('new')) return 'created';
  return 'updated';
}

function metricCounts(items) {
  const counts = { updates: 0, assignments: 0, conversions: 0 };
  items.forEach((log) => {
    const a = (log.action || '').toLowerCase();
    if (a.includes('update') || a.includes('edit') || a.includes('modify')) counts.updates += 1;
    if (a.includes('assign') || a.includes('reassign')) counts.assignments += 1;
    if (a.includes('convert')) counts.conversions += 1;
  });
  return counts;
}

function formatChanges(changes) {
  if (!changes || typeof changes !== 'object') return 'No field-level changes captured.';
  const entries = Object.entries(changes);
  if (!entries.length) return 'No field-level changes captured.';
  return entries
    .map(([field, diff]) => {
      const from = diff?.from ?? '—';
      const to = diff?.to ?? '—';
      return `${field}: ${from} → ${to}`;
    })
    .join('\n');
}

function shortId(value = '') {
  if (!value) return '—';
  if (value.length <= 10) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function ActivitiesPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    actor_id: '',
    from: '',
    to: '',
  });
  const [applied, setApplied] = useState({});

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    async function load() {
      try {
        const params = { page, limit: 20, ...applied };
        const res = await fetchAuditLogs(params);
        if (!mounted) return;
        setLogs(Array.isArray(res.data) ? res.data : []);
        setPagination(res.pagination || null);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || 'Unable to load activities.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [page, applied]);

  const getActorLabel = (log) => {
    if (log.actor_username) {
      return log.actor_username;
    }

    if (!log.actor_id) {
      return 'System';
    }

    if (log.actor_email) {
      return log.actor_email.split('@')[0];
    }

    return shortId(log.actor_id);
  };

  const getEntityLabel = (log) => {
    if (!log.entity_type) {
      return '—';
    }

    if (log.entity_type === 'lead') {
      const leadName = log.entity_display || shortId(log.entity_id);
      return `lead/${leadName}`;
    }

    if (!log.entity_id) {
      return `${String(log.entity_type).toLowerCase()}/—`;
    }

    return `${String(log.entity_type).toLowerCase()}/${shortId(log.entity_id)}`;
  };

  const metrics = useMemo(() => {
    const counts = metricCounts(logs);
    return {
      total: pagination?.total_items ?? logs.length,
      updates: counts.updates,
      assignments: counts.assignments,
      conversions: counts.conversions,
    };
  }, [logs, pagination]);

  const applyFilters = () => {
    const params = {};
    if (filters.entity_type) params.entity_type = filters.entity_type;
    if (filters.action) params.action = filters.action.trim();
    if (filters.actor_id) params.actor_id = filters.actor_id.trim();
    if (filters.from) params.from = new Date(filters.from).toISOString();
    if (filters.to) params.to = new Date(`${filters.to}T23:59:59`).toISOString();
    setApplied(params);
    setPage(1);
  };

  const onChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="dashboard-page">
      <div className="page-title-row">
        <h2>Activities</h2>
      </div>

      <section className="dashboard-kpis activities-kpis">
        <article className="dashboard-kpi-card activities-kpi-card activities-kpi-card--total">
          <span>Total Events</span>
          <strong>{metrics.total}</strong>
        </article>
        <article className="dashboard-kpi-card activities-kpi-card activities-kpi-card--updates">
          <span>Lead Updates</span>
          <strong>{metrics.updates}</strong>
        </article>
        <article className="dashboard-kpi-card activities-kpi-card activities-kpi-card--assignments">
          <span>Assignments</span>
          <strong>{metrics.assignments}</strong>
        </article>
        <article className="dashboard-kpi-card activities-kpi-card activities-kpi-card--conversions">
          <span>Conversions</span>
          <strong>{metrics.conversions}</strong>
        </article>
      </section>

      <div className="filter-bar">
        <select name="entity_type" className="filter-select" value={filters.entity_type} onChange={onChange}>
          <option value="">All Entity Types</option>
          <option value="lead">Lead</option>
          <option value="agent">Agent</option>
          <option value="user">User</option>
        </select>
        <input
          name="action"
          className="filter-input"
          placeholder="Action contains..."
          value={filters.action}
          onChange={onChange}
        />
        <input
          name="actor_id"
          className="filter-input"
          placeholder="Actor ID..."
          value={filters.actor_id}
          onChange={onChange}
        />
        <label className="filter-date-label">
          From
          <input name="from" type="date" className="filter-input" value={filters.from} onChange={onChange} />
        </label>
        <label className="filter-date-label">
          To
          <input name="to" type="date" className="filter-input" value={filters.to} onChange={onChange} />
        </label>
        <button type="button" className="primary-btn" onClick={applyFilters}>Apply</button>
      </div>

      {loading ? <p>Loading activities...</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      {!loading && !error && (
        <>
          <div className="dashboard-panel dashboard-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Activity</th>
                  <th>Entity</th>
                  <th>Actor</th>
                  <th>Reason</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.length ? (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {log.created_at ? new Date(log.created_at).toLocaleString('en-IN') : '—'}
                      </td>
                      <td>
                        <span className={`activity-pill activity-pill--${toneForAction(log.action)}`}>
                          {toLabel(log.action)}
                        </span>
                      </td>
                      <td>
                        <span>{getEntityLabel(log)}</span>
                      </td>
                      <td>
                        <span>{getActorLabel(log)}</span>
                      </td>
                      <td>{log.reason || '—'}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="icon-action-btn"
                            title="View details"
                            aria-label="View details"
                            data-tooltip="View details"
                            onClick={() => setSelected(log)}
                          >
                            <FiEye />
                          </button>
                          {log.entity_type === 'lead' && log.entity_id ? (
                            <button
                              type="button"
                              className="icon-action-btn"
                              title="Open lead"
                              aria-label="Open lead"
                              data-tooltip="Open lead"
                              onClick={() => navigate(`/leads/${log.entity_id}/edit`)}
                            >
                              <FiEdit2 />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6">No activities found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 ? (
            <div className="pagination-row">
              <button
                className="ghost-btn"
                disabled={!pagination.has_prev_page}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Previous
              </button>
              <span>
                Page {pagination.current_page} of {pagination.total_pages}
                {' '}({pagination.total_items} total)
              </span>
              <button
                className="ghost-btn"
                disabled={!pagination.has_next_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          ) : null}
        </>
      )}

      {selected ? (
        <>
          <div className="drawer-backdrop" onClick={() => setSelected(null)} />
          <aside className="lead-drawer">
            <div className="drawer-header">
              <h3>{toLabel(selected.action)}</h3>
              <button className="modal-close" onClick={() => setSelected(null)} aria-label="Close">x</button>
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Event Details</p>
              <div className="drawer-row">
                <span className="drawer-label">When</span>
                <span className="drawer-value">{selected.created_at ? new Date(selected.created_at).toLocaleString('en-IN') : '—'}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Entity</span>
                <span className="drawer-value">{getEntityLabel(selected)}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Actor</span>
                <span className="drawer-value">{getActorLabel(selected)}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Reason</span>
                <span className="drawer-value">{selected.reason || '—'}</span>
              </div>
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Change Set</p>
              <pre className="activity-change-pre">{formatChanges(selected.changes)}</pre>
            </div>

            <div className="drawer-actions">
              {selected.entity_type === 'lead' && selected.entity_id ? (
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => navigate(`/leads/${selected.entity_id}/edit`)}
                >
                  Open Lead
                </button>
              ) : null}
              <button type="button" className="ghost-btn" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
          </aside>
        </>
      ) : null}
    </div>
  );
}

export default ActivitiesPage;
